import React, { useEffect, useRef, useState } from 'react';

import { BankAccountCreate, StoreGroupExtended } from '@flipdish/api-client-typescript';
import Grid from '@mui/material/Grid';
import { type FormikProps, Form, withFormik } from 'formik';
import { electronicFormatIBAN, extractBIC, extractIBAN } from 'ibantools';
import { isEmpty } from 'lodash';
import { Translate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { compose } from 'recompose';

import AssignStoresField from '@fd/ui/Filter/AssignStoresField';

import { storeGroupActions } from '../../../../actions/storegroup.actions';
import { getStoreGroups } from '../../../../selectors/storegroup.selector';
import Button from '../../../../ui/Button/Button';
import Spacer from '../../../../ui/Spacer';
import { bankingSelectors } from '../banking.selectors';
import { nonVATSupportedCountries } from '../constants';
import BankAddressFormFields from './fields/BankAddressFormFields';
import BankHolderAddressFormFields from './fields/BankHolderAddressFormFields';
import DynamicFormFields from './fields/DynamicFormFields';
import GridDivider from './GridDivider';
import PreventNavigation from './PreventNavigation';

export type AddAccountFormProps = {
  initialValues?: BankAccountCreate;
  submit: (changes) => Promise<void>;
  onSubmitSuccess: (flag: boolean) => void;
  isSubmitSuccess?: boolean;
  prePopulateVatNumber?: string;
  gbIbanAutofillEnabled?: boolean;
};

export type FormValues = ReturnType<typeof getDefaultFormState>;
export const getDefaultFormState = (
  props: AddAccountFormProps & MappedProps
): BankAccountCreate => {
  const { initialValues = {}, orgCountryCode } = props;

  const defaultState = {
    BankAddress: initialValues.BankAddress || '',
    BankCountryCode: initialValues.BankCountryCode || orgCountryCode || '',
    AccountHolderAddress: initialValues.AccountHolderAddress || '',
    AccountHolderCountryCode: initialValues.AccountHolderCountryCode || '',
    VatNumber: initialValues.VatNumber || '',
    AccountName: initialValues.AccountName || '',
    Iban: initialValues.Iban || '',
    Swift: initialValues.Swift || '',
    CurrencyCode: initialValues.CurrencyCode || BankAccountCreate.CurrencyCodeEnum.EUR,
    NationalClearingCode: initialValues.NationalClearingCode || '',
    BankName: initialValues.BankName || '',
    StoreIds: initialValues.StoreIds || [],
    BusinessType: initialValues.BusinessType || BankAccountCreate.BusinessTypeEnum.Company,
    PopulatedAccountFields: initialValues.PopulatedAccountFields || [],
  };

  return defaultState;
};

const focusableElementSelector = 'input, textarea, select, [tabindex]:not([tabindex="-1"])';
const focusDelayMs = 250;

const getFocusableErrorFieldElement = (fieldElement: HTMLElement | null) => {
  if (!fieldElement) {
    return null;
  }

  if (fieldElement.matches(focusableElementSelector)) {
    return fieldElement;
  }

  return fieldElement.querySelector<HTMLElement>(focusableElementSelector) || fieldElement;
};

const focusAfterScroll = (
  fieldElement: HTMLElement,
  focusTimeoutRef: React.MutableRefObject<number | null>
) => {
  if (focusTimeoutRef.current) {
    window.clearTimeout(focusTimeoutRef.current);
  }

  focusTimeoutRef.current = window.setTimeout(() => {
    try {
      fieldElement.focus({ preventScroll: true });
    } catch {
      fieldElement.focus();
    }
  }, focusDelayMs);
};

type Props = FormikProps<FormValues> & AddAccountFormProps & MappedProps & MappedDispatch;
const AddAccountForm = (props: Props) => {
  const {
    errors,
    loadAllForApp,
    selectedCountry,
    countryFields,
    isSubmitSuccess = false,
    prePopulateVatNumber,
    submitCount,
    values,
    setFieldValue,
    touched,
  } = props;
  const [isReady, setIsReady] = useState(false);
  const lastAutoFilledVatRef = useRef<string | null>(null);
  const lastScrolledSubmitCountRef = useRef(0);
  const focusTimeoutRef = useRef<number | null>(null);

  const hasValidationErrors = submitCount > 0 && !isEmpty(errors);

  useEffect(() => {
    loadAllForApp().then(() => setIsReady(true));
  }, []);

  // Only use the prepopulateVatNumber to set vat field if the user hasn't already interacted with that field and the field is still empty.
  useEffect(() => {
    if (!prePopulateVatNumber) return;
    if (values?.VatNumber) return;
    if (touched?.VatNumber) return;

    if (lastAutoFilledVatRef.current === prePopulateVatNumber) return;

    void setFieldValue('VatNumber', prePopulateVatNumber, false);
    lastAutoFilledVatRef.current = prePopulateVatNumber;
  }, [prePopulateVatNumber, values?.VatNumber, touched?.VatNumber, setFieldValue]);

  useEffect(() => {
    if (!hasValidationErrors || submitCount <= lastScrolledSubmitCountRef.current) {
      return;
    }

    const invalidFieldElement = document.querySelector<HTMLElement>('[aria-invalid="true"]');
    const focusableErrorFieldElement = getFocusableErrorFieldElement(invalidFieldElement);

    if (invalidFieldElement) {
      invalidFieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    if (focusableErrorFieldElement?.focus) {
      focusAfterScroll(focusableErrorFieldElement, focusTimeoutRef);
    }

    lastScrolledSubmitCountRef.current = submitCount;
  }, [hasValidationErrors, submitCount]);

  useEffect(() => {
    return () => {
      if (focusTimeoutRef.current) {
        window.clearTimeout(focusTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Form>
      <PreventNavigation when={props.dirty && !isSubmitSuccess} />

      <Grid container>
        <Grid item xs={12}>
          <BankAddressFormFields />
        </Grid>
        <GridDivider />
        {isReady ? <DynamicFormFields countryFields={countryFields?.FieldDefinitions} /> : null}
        <GridDivider />
        <Grid item xs={12}>
          <BankHolderAddressFormFields
            nonVATCountry={nonVATSupportedCountries}
            country={selectedCountry.value}
          />
        </Grid>
        <GridDivider />
        <Grid item xs={12}>
          <AssignStoresField
            dataFd="banking-store-select"
            description="Assign_stores_description"
            fieldLabel="Assign_stores"
            fieldName="StoreIds"
            placeholder=""
            showValueEndAdornment
          />
        </Grid>
        <Grid item xs={12}>
          <Spacer size={16} variant="horizontal">
            <Grid container justifyContent="flex-end" alignContent="center" spacing={2}>
              <Grid item>
                <Button
                  type="submit"
                  color="primary"
                  fdKey="btn-bannk-account-create"
                  variant="contained"
                  disabled={props.isSubmitting}
                >
                  <Translate id="Add_account" />
                </Button>
              </Grid>
            </Grid>
          </Spacer>
        </Grid>
      </Grid>
    </Form>
  );
};

type MappedProps = ReturnType<typeof mapStateToProps>;
function mapStateToProps(state: AppState) {
  const { banking, currentApp } = state;
  const storeGroups = getStoreGroups(state);
  const currency = storeGroups.length
    ? storeGroups[0].Currency
    : StoreGroupExtended.CurrencyEnum.EUR;
  const selectedCountry = banking.selectedCountry;
  const countryFields = bankingSelectors.getCountriesWithFieldDefinitions(
    selectedCountry.value,
    state
  );
  const orgCountryCode = currentApp?.CountryId || '';
  return {
    selectedCountry,
    storeGroups,
    currency,
    countryFields,
    orgCountryCode,
  };
}

type MappedDispatch = ReturnType<typeof mapDispatchToProps>;
function mapDispatchToProps(dispatch: ThunkDispatch) {
  return {
    loadAllForApp: () => dispatch(storeGroupActions.loadAllForApp({})),
  };
}

export default compose<Props, AddAccountFormProps>(
  connect(mapStateToProps, mapDispatchToProps),
  withFormik<Props, FormValues>({
    displayName: 'AddAccountForm',
    mapPropsToValues: (props) => {
      return getDefaultFormState(props);
    },
    handleSubmit: (values, formikBag) => {
      const isStripeSupportedCountry =
        formikBag.props.countryFields.SupportType === 'supported-by-stripe-cc';

      const isGbIbanFeature =
        formikBag.props.gbIbanAutofillEnabled && values.BankCountryCode === 'GB';

      // GB IBAN feature: strip the virtual IBAN field from PopulatedAccountFields
      // before submitting. The IBAN field is injected by the UI only (Zeus does
      // not define it for GB). When present, BankAccountCreateAction.ValidateBankData
      // reads it, finds no Swift BIC for GB, and returns ErrorCode 2800
      // "Invalid Swift Country" (BankAccountInvalidIbanOrSwift).
      let populatedAccountFields = values.PopulatedAccountFields || [];
      if (isGbIbanFeature) {
        populatedAccountFields = (populatedAccountFields as Array<{ DisplayName?: string }>).filter(
          (f) => f.DisplayName !== 'IBAN'
        ) as typeof populatedAccountFields;
      }

      const bAccount: BankAccountCreate = {
        ...values,
        PopulatedAccountFields: populatedAccountFields,
        Iban: electronicFormatIBAN(values.Iban as string),
      };
      formikBag.props
        .submit(bAccount)
        .then(() => {
          formikBag.resetForm(); // reset dirty state
          formikBag.props.onSubmitSuccess(isStripeSupportedCountry);
        })
        .catch((err) => {
          formikBag.setSubmitting(false);
          if (err.message === 'could_not_create_bankaccount_bankcountry_not_supported') {
            formikBag.setFieldError('BankCountryCode', err.message);
          }
        });
    },
    validate: (values, props) => {
      const errors: { [K in keyof BankAccountCreate]: string } = {};
      if (values.Iban && values.Swift) {
        const ibanMeta = extractIBAN(values.Iban);
        const bicMeta = extractBIC(values.Swift);

        if (ibanMeta.valid && bicMeta.valid) {
          if (ibanMeta.countryCode !== bicMeta.countryCode) {
            errors.Swift = 'Invalid-input';
          }
          if (values.BankCountryCode && values.BankCountryCode !== ibanMeta.countryCode) {
            errors.BankCountryCode = 'Invalid-input';
          }
        }
      }

      // GB IBAN feature: if the virtual IBAN field has a value whose country
      // prefix doesn't match the selected bank country, surface an error on
      // the Country field. Guarded by the same bankAccount_gbIbanAutofill flag.
      if (props.gbIbanAutofillEnabled && values.BankCountryCode === 'GB') {
        const gbIbanVal =
          (
            values.PopulatedAccountFields as Array<{
              DisplayName?: string;
              Value: string;
            }>
          )?.find((f) => f.DisplayName === 'IBAN')?.Value || '';
        if (gbIbanVal) {
          // Compare the two-letter country prefix directly rather than relying
          // on ibanMeta.valid, so the error fires even when the checksum is
          // wrong (e.g. a 22-char DE IBAN with a bad checksum still has a DE
          // prefix that clearly doesn't match GB).
          const ibanCountry = gbIbanVal.slice(0, 2);
          if (ibanCountry && ibanCountry !== values.BankCountryCode) {
            errors.BankCountryCode = (
              <Translate id="GB_Bank_account_IBAN_country_mismatch" />
            ) as unknown as string;
          }
        }
      }

      return errors;
    },
  })
)(AddAccountForm);
